/**
 * GvG planner — feature-local install entrypoint.
 *
 * `installGvgPlanner(app, router, opts)` registers the planner feature on
 * a host-level Vue app + Vue Router. After Phase 7 the planner owns:
 *
 *   1. Its feature routes (`@/features/gvg-planner/routes.ts`) — added via
 *      `router.addRoute(record)` so the host router can be a thin shell.
 *
 *   2. The `boardChanged` GraphQL subscription cascade. The watcher
 *      itself still lives inside `gvgPlannerStore` (the store's
 *      `echo.buildEchoKey` + `fetchBoard` forward-decl refer to internal
 *      setup-time handles that would be hard to thread out cleanly).
 *      See the "WS subscription note" inline below.
 *
 *   3. The guild-change cascade (`fetchBoard` + `loadRaids` +
 *      `fetchPlayerTags` whenever the active Discord guild id changes,
 *      including on first mount when the id is restored from
 *      localStorage). Wired here as a `watch` with `immediate: true`
 *      over `guild.activeDiscordGuildId` rather than via
 *      `onActiveGuildChange(cb)` because the persisted id does not
 *      re-flow through `setActive()` on a fresh page load — without
 *      `immediate: true`, direct navigation to `/templates`, `/calendar`,
 *      or `/:guildId/plan/:raidId` would leave the board/raid list empty until
 *      the user re-picked a guild. The watch replaces the cascade
 *      registration entirely (the registry stays exported for any
 *      future first-party subscriber but currently has no callers).
 *
 *   4. The nav-link descriptors contributed to the host registry
 *      (`opts.contributeNav(gvgPlannerNavLinks({ router }))`).
 *
 *   5. The initial `loadAvailableGuilds()` bootstrap (auto-switch on
 *      mismatch + first-time hydration) — the call used to live in
 *      `AppHeader.onMounted`; moving it here means the planner surface
 *      is fully mounted (store available, watch cascade wired, nav
 *      contributed) before the host chrome's `onMounted` fires.
 */
import type { App } from 'vue'
import { watch } from 'vue'
import type { Router } from 'vue-router'

import { useGuild } from '@/shared/ports/guild'
import { APP_ROUTE_NAMES } from '@/shared/constants/routes'
import { useGvgPlannerStore } from './stores/gvgPlannerStore'
import { FEATURE_ROUTE_NAMES } from './constants/routes'
import { featureRoutes } from './routes'
import { registerPlannerRouteGuards } from './routerGuards'
import { gvgPlannerNavLinks } from './nav'
import type { NavLinkDescriptor } from '@/app/nav'

export interface GvgPlannerInstallOptions {
  /**
   * The host registry's append function. Usually `addNavLinks` from
   * `@/app/nav`; passed as a callback so this module doesn't have to
   * import the host's mutable state directly.
   */
  readonly contributeNav: (entries: ReadonlyArray<NavLinkDescriptor>) => void
}

export function installGvgPlanner(
  _app: App,
  router: Router,
  opts: GvgPlannerInstallOptions,
): void {
  // 1. Feature routes
  for (const route of featureRoutes) {
    router.addRoute(route)
  }

  // 1b. Global beforeEach for the parameterized planner route.
  //     Lives on the router (not the route record) so it fires on
  //     param-only updates too — see `routerGuards.ts` for the
  //     rationale and the regression test under
  //     `apps/frontend/src/router/__tests__/plannerRaidNavigation.test.ts`
  //     (the `src/router/__tests__/` path is intentional — it carries
  //     the legacy route-fixture stdlib that was lifted out of the
  //     host router in Phase 7).
  registerPlannerRouteGuards(router)

  // 2. Authorized-guild cascade. A persisted guild id is only a preference,
  //    not a capability: wait for the host's fresh `canView` projection before
  //    any board, calendar, tag, or raid request runs. Watching the boolean
  //    projection (rather than the guild array itself) avoids re-fetching the
  //    planner on every MyGuilds refresh while still waking once a newly
  //    granted access role makes the active guild viewable.
  const guild = useGuild()
  watch(
    [
      () => guild.activeDiscordGuildId.value,
      () => guild.loading.value,
      () => guild.availableGuilds.value.some(
        (candidate) => candidate.discordGuildId === guild.activeDiscordGuildId.value,
      ),
    ],
    ([id, loadingGuilds, canView], previous) => {
      const previousId = previous?.[0] ?? '0'
      const previouslyViewable = previous?.[2] ?? false
      const changedGuild = id !== previousId
      const store = useGvgPlannerStore()
      if (id === '0') {
        if (changedGuild || previouslyViewable) {
          void store.selectRaid(null)
          void store.fetchBoard()
          void store.loadRaids()
          void store.fetchPlayerTags()
          void store.refreshCalendarWindow()
          // A host fail-closed clear can happen while a planner route is
          // already mounted (for example, when its live subscription reports
          // FORBIDDEN after a Discord role is removed). Route away from that
          // mounted surface so the locked-guild request controls can render.
          if (router.currentRoute.value.name !== FEATURE_ROUTE_NAMES.Onboarding) {
            void router.replace({ name: FEATURE_ROUTE_NAMES.Onboarding })
          }
        }
        return
      }
      if (loadingGuilds || !canView) return
      if (!changedGuild && previouslyViewable) return

      // A raid snapshot belongs to its source guild. Drop it before any
      // authorized replacement request starts so a switch can never leave a
      // previous guild's rooms, stages, or tactical-map placements on screen.
      void store.selectRaid(null)
      void store.fetchBoard()
      void store.loadRaids()
      void store.fetchPlayerTags()
      // RaidCalendarView only loads on mount or week navigation. Refreshing
      // the current window here keeps the visible week scoped to the newly
      // selected guild without forcing the user to leave and re-enter it.
      void store.refreshCalendarWindow()
      if (
        previousId === '0'
        && router.currentRoute.value.name === FEATURE_ROUTE_NAMES.Onboarding
      ) {
        void router.replace({
          name: APP_ROUTE_NAMES.Calendar,
          params: { guildId: id },
        })
      }
    },
    { immediate: true },
  )

  // 3. Bootstrap the guild list AND auto-switch on mismatch. The
  //    legacy call (Phase 6 era) did this work inside
  //    `setActiveDiscordGuildId`; Phase 7 lifts it onto install so the
  //    cascade registry stays in sync without a duplicate
  //    `setActiveDiscordGuildId` call. The store still exposes a thin
  //    legacy action for the GuildSelector (which still calls
  //    `setActiveDiscordGuildId(snowflake)`); the cascade here is what
  //    makes the cascade fire on a selector-driven switch.
  void useGvgPlannerStore().loadAvailableGuilds()

  // 4. Contribute nav descriptors (Calendar / Raid plan / Guild settings /
  //    Audit log / Onboarding). The descriptor set is computed once;
  //    per-descriptor `visible` computeds stay live across the app
  //    lifetime.
  opts.contributeNav(gvgPlannerNavLinks({ router }))

  // ── WS subscription note (Option A from the Phase 7 plan) ────────────────
  // The `boardChanged` GraphQL subscription watcher stays inside
  // `gvgPlannerStore` instead of being wired here. Two reasons:
  //
  //   a. The watcher relies on `echo.consumeEcho(...)` and the
  //      store-internal `subscription` / `liveSyncError` setup-time
  //      refs; moving them out would require exposing additional
  //      handles on the store's public API just for the install path.
  //
  //   b. `gvgPlannerStore` is the one Pinia store the WS errors
  //      surface through (`liveSyncError`), so the watcher's lifecycle
  //      is naturally tied to the store's lifetime.
  //
  // Both keep the cascade-stripped `beforeEach` global guard in
  //     `routerGuards.ts` running unchanged, and mean
  //     Phase 7's install entrypoint only owns the side effects above —
  //     not the subscription itself.
}
